import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LifebuoyIcon, PaperClipIcon, PaperAirplaneIcon, PlusIcon, XMarkIcon,
  LockClosedIcon, MagnifyingGlassIcon, ComputerDesktopIcon, KeyIcon,
} from '@heroicons/react/24/outline';
import {
  useTicketMeta, useMyTickets, useTicketQueue, useTicketStats, useTicket,
  useRaiseTicket, useReplyToTicket, useUpdateTicket, useAttachToTicket, useAttachment,
} from '../hooks/useItTickets';

// IT Support. One screen for everybody: your own tickets, plus the queue if you
// are on the IT team. Whether you are is decided by the SERVER (meta.isAgent) —
// the agent group is env-configured and the client has no business knowing it.

const STATUS_LABEL = {
  new: 'New', in_progress: 'In progress', waiting_on_user: 'Waiting on you',
  resolved: 'Resolved', closed: 'Closed',
};
const STATUS_TONE = {
  new: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-gray-100 text-gray-600',
};
const PRIORITY_TONE = {
  urgent: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800',
  normal: 'bg-gray-100 text-gray-600', low: 'bg-gray-100 text-gray-500',
};

const fmt = (v) => (v ? new Date(v).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

// Compressed before upload for the same reason the delivery-note photo is: a
// raw phone screenshot is several megabytes and the cap is 6MB.
function readFile(file, maxPx = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      if (file.type === 'application/pdf') { resolve(reader.result); return; }
      const img = new Image();
      img.onerror = () => reject(new Error('That file is not an image or PDF.'));
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Pill({ tone, children }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{children}</span>;
}

// The same three choices as the engineer app, with one swap: office staff live
// in the platform, not the mobile app, so 'Nano platform' takes the slot 'App
// Support' holds on FSM. Each carries its own wording -- one "What happened?"
// prompt is wrong for half of them, since somebody requesting access has not
// had anything happen.
const CATEGORY_CARDS = [
  {
    key: 'platform',
    label: 'Platform Support',
    Icon: ComputerDesktopIcon,
    summary: 'What is going wrong?',
    summaryHint: 'e.g. Inspection report will not open',
    detail: 'What were you doing when it happened?',
    detailHint: 'Which page you were on, what you expected, and what it did instead.',
  },
  {
    key: 'access_request',
    label: 'Access Request',
    Icon: KeyIcon,
    summary: 'What do you need access to?',
    summaryHint: 'e.g. SHEQ SharePoint site',
    detail: 'What do you need to do with it?',
    detailHint: 'Knowing why lets IT give you the right level rather than guessing.',
  },
  {
    key: 'general',
    label: 'IT Support',
    Icon: LifebuoyIcon,
    summary: 'What do you need?',
    summaryHint: 'e.g. Laptop will not charge',
    detail: 'Tell us a bit more',
    detailHint: 'Anything that helps IT sort it without having to come back and ask.',
  },
];

// Worded as the person experiences it, not as a support desk grades it.
const URGENCY = [
  { key: 'normal', label: 'Soon', off: 'border-emerald-300 bg-white text-emerald-800', on: 'border-emerald-600 bg-emerald-600 text-white' },
  { key: 'high', label: 'I am stuck', off: 'border-amber-300 bg-white text-amber-800', on: 'border-amber-500 bg-amber-500 text-white' },
  { key: 'urgent', label: 'Cannot work', off: 'border-red-300 bg-white text-red-700', on: 'border-red-600 bg-red-600 text-white' },
];

function RaiseForm({ onClose }) {
  const raise = useRaiseTicket();
  const attach = useAttachToTicket();
  const [cat, setCat] = useState(null);
  // priority starts EMPTY on purpose. A preselected default is our guess wearing
  // the raiser's name, and would quietly become the answer on every ticket
  // nobody thought about.
  const [form, setForm] = useState({ subject: '', body: '', priority: '' });
  const [shots, setShots] = useState([]);
  const [err, setErr] = useState('');
  const [stage, setStage] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Captured rather than asked for: nobody should have to know their own browser
  // version to report a bug.
  const context = `${window.location.pathname} · ${navigator.userAgent.slice(0, 180)}`;
  const chosen = CATEGORY_CARDS.find((c) => c.key === cat);

  async function addShot(file) {
    setErr('');
    try {
      // Resolved before the state update: an updater callback is not async.
      const next = await readFile(file);
      setShots((l) => [...l, next]);
    } catch (e) { setErr(e.message); }
  }

  async function submit() {
    setErr('');
    if (!form.subject.trim()) { setErr('Give it a one-line summary so IT can see what it is.'); return; }
    if (!form.body.trim()) { setErr('Add a bit of detail — it saves IT coming back to ask.'); return; }
    if (!form.priority) { setErr('Let IT know how urgent it is.'); return; }
    try {
      setStage('Sending…');
      const t = await raise.mutateAsync({ ...form, category: cat, context });
      for (let i = 0; i < shots.length; i += 1) {
        setStage(`Attaching file ${i + 1} of ${shots.length}…`);
        await attach.mutateAsync({ id: t.id, file: shots[i], name: `attachment-${i + 1}` });
      }
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not send that. Try again.');
      setStage('');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-10">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">What do you need help with?</h2>
            <p className="text-sm text-gray-500">Pick the closest one — IT can move it if it lands in the wrong place.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Three things you can see beat a dropdown of three things you cannot. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {CATEGORY_CARDS.map(({ key, label, Icon }) => (
            <button key={key} type="button" onClick={() => setCat(key)}
              className={'flex items-center gap-3 rounded-md border p-4 text-left transition ' + (cat === key
                ? 'border-brand bg-brand-light ring-1 ring-brand'
                : 'border-gray-300 bg-white hover:border-brand hover:bg-brand-light/40')}>
              <span className={'flex h-10 w-10 shrink-0 items-center justify-center rounded-md ' + (cat === key ? 'bg-brand' : 'bg-gray-100')}>
                <Icon className={'h-5 w-5 ' + (cat === key ? 'text-white' : 'text-gray-500')} />
              </span>
              <span className="text-sm font-semibold text-gray-900">{label}</span>
            </button>
          ))}
        </div>

        {/* Nothing else appears until there is something to say it about. */}
        {chosen ? (
          <>
            <label className="mt-6 block">
              <span className="mb-1 block text-sm font-semibold text-gray-800">{chosen.summary}</span>
              <input value={form.subject} onChange={set('subject')} maxLength={200}
                placeholder={chosen.summaryHint}
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm" />
            </label>

            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-semibold text-gray-800">{chosen.detail}</span>
              <span className="mb-1 block text-xs text-gray-500">{chosen.detailHint}</span>
              <textarea value={form.body} onChange={set('body')} rows={5}
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm" />
            </label>

            <div className="mt-4">
              <span className="mb-1 block text-sm font-semibold text-gray-800">
                Screenshot <span className="font-normal text-gray-500">— optional, but it saves a lot of back and forth</span>
              </span>
              {shots.length ? (
                <div className="mb-2 grid grid-cols-4 gap-2">
                  {shots.map((src, i) => (
                    /* eslint-disable-next-line react/no-array-index-key */
                    <div key={i} className="relative">
                      {/^data:application\/pdf/.test(src) ? (
                        <div className="flex h-24 w-full items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500">
                          PDF
                        </div>
                      ) : (
                        <img src={src} alt={`Attachment ${i + 1}`} className="h-24 w-full rounded-md border border-gray-200 object-cover" />
                      )}
                      <button type="button" onClick={() => setShots((l) => l.filter((_, j) => j !== i))}
                        aria-label={`Remove attachment ${i + 1}`}
                        className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/80 text-xs font-bold text-white">
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 py-4 text-sm font-semibold text-gray-600 hover:border-brand hover:text-brand">
                <PaperClipIcon className="h-5 w-5 text-brand" />
                {shots.length ? 'Add another' : 'Add a screenshot or PDF'}
                <input type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) addShot(e.target.files[0]);
                    // Cleared so the same file can be chosen twice running.
                    e.target.value = '';
                  }} />
              </label>
            </div>

            <div className="mt-4">
              <span className="mb-1 block text-sm font-semibold text-gray-800">How urgent is it?</span>
              <div className="grid max-w-md grid-cols-3 gap-2">
                {URGENCY.map((u) => (
                  <button key={u.key} type="button" onClick={() => setForm((f) => ({ ...f, priority: u.key }))}
                    className={'rounded-md border px-2 py-2 text-sm font-semibold ' + (form.priority === u.key ? u.on : u.off)}>
                    {u.label}
                  </button>
                ))}
              </div>
            </div>

            {err ? (
              <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
                Cancel
              </button>
              <button type="button" disabled={Boolean(stage)} onClick={submit}
                className="rounded-md bg-brand px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {stage || 'Send to the IT department'}
              </button>
            </div>
            <p className="mt-2 text-right text-xs text-gray-500">
              You will get a reply on this ticket — you do not need to chase it.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Attachment({ attachment }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useAttachment(attachment.id, open);
  const isPdf = attachment.mime_type === 'application/pdf';
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand">
        <PaperClipIcon className="h-3.5 w-3.5" />
        {attachment.original_name || 'Attachment'}
      </button>
      {open && (
        <div className="mt-2">
          {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
          {data?.file && (isPdf
            ? <a href={data.file} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand underline">Open PDF</a>
            : <img src={data.file} alt={attachment.original_name || 'Attachment'}
                className="max-h-96 rounded-md border border-gray-200" />)}
        </div>
      )}
    </div>
  );
}

function TicketDetail({ id, isAgent, meta, onClose }) {
  const { data: t, isLoading } = useTicket(id);
  const reply = useReplyToTicket();
  const update = useUpdateTicket();
  const attach = useAttachToTicket();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef(null);

  // Keep the newest message in view. A long ticket otherwise opens at the
  // original report, which is the least useful part by the time IT is working it.
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [t?.messages?.length]);

  // Attachments are stored against the ticket and optionally a message, so group
  // them once rather than filtering inside the render loop.
  const attachments = useMemo(() => {
    const byMessage = new Map();
    (t?.attachments || []).forEach((a) => {
      const k = a.message_id || 'ticket';
      byMessage.set(k, [...(byMessage.get(k) || []), a]);
    });
    return byMessage;
  }, [t]);

  async function addFile(file) {
    setErr('');
    try { attach.mutate({ id, file: await readFile(file), name: file.name }); }
    catch (e) { setErr(e.message); }
  }

  const assignSelf = () => update.mutate({ id, assigned_to: 'me', assigned_name: 'me' });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-8">
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
        {isLoading || !t ? (
          <p className="p-6 text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            <div className="flex items-start justify-between border-b border-gray-200 p-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-gray-900">{t.reference}</span>
                  <Pill tone={STATUS_TONE[t.status]}>{STATUS_LABEL[t.status]}</Pill>
                  <Pill tone={PRIORITY_TONE[t.priority]}>{t.priority}</Pill>
                  <span className="text-xs text-gray-500">{meta?.categories?.[t.category]}</span>
                </div>
                <h2 className="mt-1 text-lg font-bold text-gray-900">{t.subject}</h2>
                <p className="text-sm text-gray-500">
                  {t.raised_by_name} · raised {fmt(t.created_at)}
                  {t.assigned_name ? ' · with ' + t.assigned_name : ''}
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {isAgent && (
              <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <select value={t.status} onChange={(e) => update.mutate({ id, status: e.target.value })}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                  {(meta?.statuses || []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
                <select value={t.priority} onChange={(e) => update.mutate({ id, priority: e.target.value })}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                  {(meta?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={t.category} onChange={(e) => update.mutate({ id, category: e.target.value })}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                  {Object.entries(meta?.categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {t.assigned_to ? (
                  <button type="button" onClick={() => update.mutate({ id, assigned_to: null, assigned_name: null })}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700">
                    Unassign
                  </button>
                ) : (
                  <button type="button" onClick={assignSelf}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700">
                    Assign to me
                  </button>
                )}
              </div>
            )}

            <div className="max-h-[45vh] space-y-4 overflow-y-auto p-5">
              {t.messages.map((m) => (
                <div key={m.id} className={m.sender_type === 'user' ? '' : 'pl-6'}>
                  <div className={'rounded-lg px-3 py-2 ' + (m.is_internal
                    ? 'border border-amber-200 bg-amber-50'
                    : m.sender_type === 'user' ? 'bg-gray-100' : 'bg-brand-light')}>
                    <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                      <span className="font-semibold text-gray-700">{m.sender_name || 'Unknown'}</span>
                      <span>{fmt(m.created_at)}</span>
                      {m.is_internal ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                          <LockClosedIcon className="h-3 w-3" /> internal
                        </span>
                      ) : null}
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-800">{m.body}</p>
                    {(attachments.get(m.id) || []).map((a) => <Attachment key={a.id} attachment={a} />)}
                  </div>
                </div>
              ))}
              {(attachments.get('ticket') || []).map((a) => <Attachment key={a.id} attachment={a} />)}
              <div ref={endRef} />
            </div>

            <div className="border-t border-gray-200 p-5">
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                placeholder={internal ? 'Internal note — the person who raised this will not see it' : 'Reply…'}
                className={'w-full rounded-md border px-3 py-2 text-sm ' + (internal ? 'border-amber-300 bg-amber-50' : 'border-gray-300')} />
              {err ? <p className="mt-2 text-sm text-red-700">{err}</p> : null}
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-gray-600">
                    <PaperClipIcon className="h-4 w-4" /> Attach
                    <input type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => e.target.files && e.target.files[0] && addFile(e.target.files[0])} />
                  </label>
                  {isAgent && (
                    <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-gray-600">
                      <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                      Internal note
                    </label>
                  )}
                </div>
                <button type="button" disabled={reply.isPending || !body.trim()}
                  onClick={() => reply.mutate({ id, body, isInternal: internal },
                    { onSuccess: () => { setBody(''); setInternal(false); } })}
                  className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  <PaperAirplaneIcon className="h-4 w-4" />
                  {reply.isPending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TicketRow({ ticket, meta, onOpen }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(ticket.id)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-bold text-gray-900">{ticket.reference}</span>
            <Pill tone={STATUS_TONE[ticket.status]}>{STATUS_LABEL[ticket.status]}</Pill>
            {ticket.priority !== 'normal' ? <Pill tone={PRIORITY_TONE[ticket.priority]}>{ticket.priority}</Pill> : null}
          </span>
          <span className="mt-0.5 block truncate text-sm font-medium text-gray-900">{ticket.subject}</span>
          <span className="mt-0.5 block text-xs text-gray-500">
            {meta?.categories?.[ticket.category]}
            {' · '}{ticket.raised_by_name}
            {' · '}{fmt(ticket.updated_at)}
            {ticket.assigned_name ? ' · with ' + ticket.assigned_name : ' · unassigned'}
          </span>
        </span>
      </button>
    </li>
  );
}

function TicketList({ tickets, meta, onOpen, empty }) {
  if (!tickets) return <p className="px-4 py-8 text-sm text-gray-400">Loading…</p>;
  if (!tickets.length) {
    return <p className="px-4 py-10 text-center text-sm text-gray-400">{empty}</p>;
  }
  return <ul className="divide-y divide-gray-100">{tickets.map((t) => (
    <TicketRow key={t.id} ticket={t} meta={meta} onOpen={onOpen} />
  ))}</ul>;
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className={'text-2xl font-bold tabular-nums ' + (tone || 'text-gray-900')}>{value ?? 0}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

// The queue, for the IT team. Defaults to open tickets only: closed ones are
// history and would bury the work within a month.
function Queue({ meta, onOpen }) {
  const [filters, setFilters] = useState({ open: '1', category: '', assignee: '', q: '' });
  const { data: tickets } = useTicketQueue(filters);
  const { data: s } = useTicketStats();
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="New" value={s?.new_count} tone="text-blue-700" />
        <Stat label="In progress" value={s?.in_progress} tone="text-amber-700" />
        <Stat label="Waiting on user" value={s?.waiting} />
        <Stat label="Unassigned" value={s?.unassigned} tone="text-rose-700" />
        <Stat label="High / urgent" value={s?.urgent_open} tone="text-red-700" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={filters.q} onChange={set('q')} placeholder="Search reference, subject or person"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={filters.open} onChange={set('open')} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="1">Open only</option>
          <option value="">All tickets</option>
        </select>
        <select value={filters.category} onChange={set('category')} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All categories</option>
          {Object.entries(meta?.categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.assignee} onChange={set('assignee')} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">Anyone</option>
          <option value="unassigned">Unassigned</option>
        </select>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <TicketList tickets={tickets} meta={meta} onOpen={onOpen} empty="Nothing in the queue." />
      </div>
    </>
  );
}

export default function ItSupport() {
  const { data: meta } = useTicketMeta();
  const { data: mine } = useMyTickets();
  const [tab, setTab] = useState('mine');
  const [raising, setRaising] = useState(false);
  const [openId, setOpenId] = useState(null);
  const isAgent = Boolean(meta?.isAgent);

  // Someone who works the queue almost always wants the queue, not their own
  // two tickets. Only switches once, so a deliberate click is not overridden.
  const jumped = useRef(false);
  useEffect(() => {
    if (isAgent && !jumped.current) { jumped.current = true; setTab('queue'); }
  }, [isAgent]);

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <LifebuoyIcon className="h-9 w-9 text-brand" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">IT Support</h1>
            <p className="text-sm text-gray-500">
              Problems with the platform or the app, access requests, and anything else IT.
            </p>
          </div>
        </div>
        <button type="button" onClick={() => setRaising(true)}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white">
          <PlusIcon className="h-4 w-4" /> Raise a ticket
        </button>
      </div>

      {isAgent && (
        <div className="mb-5 flex gap-2 border-b border-gray-200">
          {[['queue', 'Queue'], ['mine', 'My tickets']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={'-mb-px border-b-2 px-4 py-2 text-sm font-semibold ' + (tab === k
                ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'queue' && isAgent ? (
        <Queue meta={meta} onOpen={setOpenId} />
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <TicketList tickets={mine} meta={meta} onOpen={setOpenId}
            empty="You have not raised any tickets." />
        </div>
      )}

      {raising && <RaiseForm onClose={() => setRaising(false)} />}
      {openId && (
        <TicketDetail id={openId} isAgent={isAgent} meta={meta} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}
